import React from 'react';

import { DisableReason } from './RecordingState';

export const L10nMeetings = /** @type {const} */ {
  meetingDashboard: {
    transcriptRegenerationDropdownTitle: 'Spoken Language',
    transcriptRegenerationDropdownSubtitle:
      'Wrong language detected? Choose a new language to reprocess the transcript',
    transcriptRegenerationTitle: 'Wrong language detected?',
    transcriptRegenerationSubTitle:
      'Select a different language to reprocess the transcript',
    transcriptRegenerationSearchPlaceholder: 'Search for a language',
    transcriptLanguageSearchNoResult: 'No matching language found',
    transcriptRegenerationRerunBanner: 'Reprocessing failed. Please try again.',
    transcriptRegenerationAvailableBanner: '/3 Re-runs available.',
    transcriptRegenerationNotAvailableBanner: 'No more Re-runs available.',
    transcriptionInProcess: 'Transcription in progress...',
    emptyTranscript: 'Transcript not available.',
    reprocess: 'Reprocess',
    impromptuInfoMsg:
      'Impromptu meetings are private by default. You can modify this later.',
    copyCurrentTime: 'Copy video URL at current time',
    generating: 'Generating...',
    gmeetLangUnsupported: {
      workspaceMember: {
        title: "Google Meet recording doesn't support this language",
        body: 'Transcript and notes may be inaccurate. To improve future accuracy, record meetings with Avoma Bot instead of cloud recording.',
        contactAdmin: 'Contact admin to change recording preference'
      },
      workspaceAdmin: {
        title: 'Google Meet was unable to transcribe in this language.',
        body: 'Transcript and notes may be inaccurate. To improve accuracy for future meetings, switch to Avoma Bot recording.',
        cta: 'Go to workspace preferences'
      },
      noWorkspace: {
        title: 'Google Meet was unable to transcribe this language',
        body: 'Transcript and notes may be inaccurate. To improve accuracy for future meetings, disconnect Google Meet & record with Bot.',
        cta: 'Disconnect Google Meet'
      }
    }
  },
  rtt: {
    title: 'Live Meeting Transcript',
    autoscroll: 'Resume Auto-Scroll',
    message: {
      paused:
        'Live transcription is inactive because the recording was paused.',
      wait: 'Live transcript will be available when the meeting begins.',
      disabled:
        'Avoma will not record this meeting as none of the meeting participants have internal meeting recording enabled.',
      gmeetDisabled:
        'Live transcript is not available for meetings recorded via the Google Meet cloud integration.'
    }
  },
  minimal: {
    pastMeetings: 'View Past Meetings',
    details: {
      completed:
        'This meeting has ended. Once the processing is complete, you can view the recording',
      link: 'here.',
      goBack: 'Back to Upcoming Meetings'
    },
    header: {
      cta: 'Take Notes for Ongoing Meeting',
      notes: 'Take notes for your upcoming meetings below or',
      notesDesktop: 'Take notes for your upcoming meetings below',
      calendarDisconnected: 'Calendar disconnected!',
      label: 'MyMeeting',
      reload: 'Reload to Check for New Meetings',
      calendarDisconnectedSteps: {
        link: 'Connect your calendar',
        title:
          'with Avoma to automatically record, transcribe, and take notes for your upcoming meetings.'
      }
    }
  },
  impromptu: {
    label: 'Meeting Title',
    videoConferencingLink: 'Video Conferencing Link',
    internal: 'Internal Meeting',
    external: 'External Meeting'
  },
  snippet: {
    regenerate: 'Regenerate Title & Description',
    snippetDisabledText:
      'Please wait until the video has been fully transcribed to create a snippet.'
  },
  savedViewSearch: {
    search: 'Search',
    placeholder: 'Search within saved view',
    showSearchResultsFor: 'Show search results for',
    sortResultsBy: 'Sort results by',
    noCategoriesFound: 'No categories found',
    smartCategorySuggestions: 'Smart Topic Suggestions',
    applyFilter: 'Apply Filters for Search',
    clearSearch: 'Clear Search',
    noKeywordsFound: 'No keywords found'
  },
  followUpEmail: {
    sendSuccessToast: 'Email sent successfully',
    sendErrorToast: (emailProvider: string) =>
      `There was an error sending email via ${emailProvider}`,
    followUpCta: 'Send email',
    meetingAgendaCta: 'Share Agenda',
    modalTitle: 'Send an email',
    meetingAgendaTitle: 'Share meeting agenda',
    followUpCtaTooltip: 'Send follow-up email',
    meetingAgendaCtaTooltip: 'Share agenda via email',
    followUpCtaTooltipDisabledNotes:
      'Please wait until AI-generated notes are available',
    followUpCtaTooltipDisabledGeneral:
      'Follow-up email generation is unavailable for this conversation',
    emailAccuracyBanner:
      'Please review the content of this follow-up email to ensure it is accurate and complete.',
    copyCta: 'Copy',
    copySuccessToast: 'Follow-up email copied successfully.',
    copyErrorToast: 'Error copying email.',
    generatingSequenceEmail: 'Generating sequence email...',
    generatingEmail: 'Generating follow-up email...',
    generationFailed: 'Email generation failed!',
    fetchingNotesEmail: 'Loading meeting notes...',
    fetchingNotesEmailFailed: 'Failed to load meeting notes!',
    tryAgainCta: 'Try Again',
    recipientPlaceholder: 'Select or enter an email address or team',
    subjectFieldPlaceholder: 'Subject',
    bodyFieldPlaceholder: 'Body',
    subjectAiIconTooltip: 'Avoma AI-generated subject',
    invalidEmail: 'Please enter a valid email address to add a new recipient.',
    discardChangesTitle: 'Discard unsaved changes?',
    discardChangesMessage:
      'You have unsaved changes. If you proceed without saving, your changes will be lost and you will not be able to recover them.',
    continueEditing: 'Continue editing',
    discardChanges: 'Discard changes',
    accessDialogTitle: 'Grant access to this conversation?',
    accessDialogInfo:
      'You included a meeting link in the email, but some recipients need access to view it.',
    generateAgain: 'Generate Again',
    addParticipantsCta: 'Add participants',
    previewEmailCta: 'Preview email',
    allParticipantsAddedTooltip:
      'All participants have been selected as recipients below',
    addParticipantsTooltip:
      'Add all participants as recipients in the "To" field',
    shareSuccessToast: 'Meeting shared.',
    shareErrorToast: 'Error sharing meeting.',
    notesEmailCtaTooltip: 'Share notes via email',
    copyNotesSuccessToast: 'Meeting notes email copied successfully.',
    notesIntroText:
      'Hi All,\n\nThanks again for your time today. Here are the notes from our conversation:\n',
    copyAgendaSuccessToast: 'Agenda copied successfully.',
    aiSummaryGatingTooltip: (planName: string) =>
      `AI Follow-up Email is available in ${planName} plan or above.`
  },
  calendar: {
    connectToCalendar: (calProvider: string) =>
      `Connect to ${calProvider} Calendar to sync meetings. It will take a few minutes to sync all your meetings.`,
    calendarSyncing: (calProvider: string) =>
      `Syncing meetings from ${calProvider} Calendar. It will take a few minutes to sync all your meetings.`
  },
  recordingState: {
    [DisableReason.disableRecordingFeatureFilter]: {
      upcoming: (
        <React.Fragment>
          Avoma will <strong>not</strong> record this meeting as no participants
          have recording feature or license.
        </React.Fragment>
      ),
      ongoing: (
        <React.Fragment>
          Avoma will <strong>not</strong> record this meeting because no
          participants had the recording feature or a valid license when the
          meeting started.
        </React.Fragment>
      )
    },
    [DisableReason.disablePhonebotFeatureFilter]: {
      upcoming: (
        <React.Fragment>
          Avoma will <strong>not</strong> record this meeting as no participants
          have phonebot feature or license.
        </React.Fragment>
      ),
      ongoing: (
        <React.Fragment>
          Avoma will <strong>not</strong> record this meeting because no
          participants had the phonebot feature or a valid license when the
          meeting started.
        </React.Fragment>
      )
    }
  },
  changeSpeaker: 'Change Speaker',
  changeSpeakerDialogInfo:
    'How would you like to update this speaker assignment?',
  methodologyMentorTitle: 'Methodology Mentor',
  methodologyMentorLoading: 'Loading methodology...',
  methodologyCardExpand: 'Switch to detailed view',
  methodologyCardCollapse: 'Switch to compact view',
  methodologyMentorDisabledBanner: {
    zoom: 'Live streaming is required to use this feature.',
    msTeams:
      'Live streaming is not available for conversations recorded in Microsoft Teams.',
    gmeet:
      'Live streaming is not available for conversations recorded via Google Meet cloud integration.'
  },
  sentimentEnglishOnly:
    'Sentiment analysis is only available for conversations in English.'
};
